import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Avatar, 
  Button, 
  TextField, 
  InputAdornment,
  CircularProgress,
  Chip,
  Divider,
  Paper
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import { API_URL } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import PartnerRequestButton from '../components/PartnerRequestButton';

const PageContainer = styled(Container)(({ theme }) => ({
  position: 'relative',
  minHeight: '100vh',
  padding: theme.spacing(4, 3),
  /* reserve space for the fixed navbar to avoid overlap */
  paddingTop: `calc(var(--nav-height) + ${theme.spacing(2)})`,
  background: 'linear-gradient(135deg, rgba(255,106,171,0.04) 0%, rgba(123,97,255,0.03) 50%, rgba(255,106,171,0.02) 100%)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    background: 'url("data:image/svg+xml,%3Csvg width=\'52\' height=\'26\' viewBox=\'0 0 52 26\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ff69b4\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M10 10c0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4v2c-3.314 0-6-2.686-6-6 0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6zm25.464-1.95l8.486 8.486-1.414 1.414-8.486-8.486 1.414-1.414z\' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat',
    opacity: 0.5,
    zIndex: 0,
  }
}));

const PageHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  position: 'relative',
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 800,
  marginBottom: theme.spacing(2),
  background: 'linear-gradient(45deg, #FF69B4 0%, #8E37D7 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  display: 'inline-block',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -10,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '80px',
    height: '4px',
    background: 'linear-gradient(45deg, #FF69B4 0%, #8E37D7 100%)',
    borderRadius: '2px',
  }
}));

const SearchBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  display: 'flex',
  alignItems: 'center',
  maxWidth: '500px',
  width: '100%',
  margin: '0 auto',
  marginBottom: theme.spacing(4),
  borderRadius: '50px',
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 32px rgba(255, 105, 180, 0.15)',
    transform: 'translateY(-2px)',
  }
}));

const UserCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  overflow: 'hidden',
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  position: 'relative',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 40px rgba(255, 105, 180, 0.2)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(45deg, #FF69B4, #8E37D7)',
  }
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 80,
  height: 80,
  border: '3px solid #fff',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  margin: '0 auto',
  marginTop: theme.spacing(2),
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.1)',
  }
}));

const UserInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
  '& svg': {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '50px',
  padding: theme.spacing(1, 3),
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(255, 105, 180, 0.3)',
  background: 'linear-gradient(45deg, #FF69B4 0%, #8E37D7 100%)',
  '&:hover': {
    background: 'linear-gradient(45deg, #FF5BA4 0%, #7E25C5 100%)',
    boxShadow: '0 6px 15px rgba(255, 105, 180, 0.4)',
    transform: 'translateY(-2px)',
  },
  transition: 'all 0.3s ease',
}));

const UserCardContent = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: theme.spacing(2, 3, 3),
  flex: 1,
}));

const UserMetaInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '400px',
  '& > *': {
    marginBottom: theme.spacing(2),
  }
}));

// Helper function to calculate age from birthday
function calculateAge(birthday) {
  if (!birthday) return '';
  const birthDate = new Date(birthday);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (users.length > 0) {
      const filtered = users.filter(user => !user.partner && user._id !== currentUser?._id);
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users, currentUser]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setUsers(response.data);
      setFilteredUsers(response.data);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch users');
      setLoading(false);
    }
  };

  const handleViewProfile = (userId) => {
    navigate(`/user/${userId}`);
  };

  const renderUserAge = (user) => {
    return user.age ? <Chip size="small" label={`${user.age} years`} /> : null;
  };

  const renderUserLocation = (user) => {
    if (user.location && user.location.coordinates) {
      return (
        <UserInfo>
          <LocationOnIcon fontSize="small" />
          <Typography variant="body2">Has location</Typography>
        </UserInfo>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <PageContainer maxWidth="lg">
        <LoadingContainer>
          <CircularProgress size={60} sx={{ color: '#FF69B4' }} />
          <Typography variant="h6" color="textSecondary">
            Loading amazing people...
          </Typography>
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer maxWidth="lg">
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={fetchUsers}
            sx={{ mt: 2 }}
          >
            Try Again
          </Button>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="lg">
      <PageHeader>
        <PageTitle variant="h2">Discover People</PageTitle>
        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
          Connect with amazing individuals in our community
        </Typography>
        <SearchBox elevation={2}>
          <TextField
            fullWidth
            placeholder="Search by name or email..."
            variant="standard"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#FF69B4' }} />
                </InputAdornment>
              ),
            }}
          />
        </SearchBox>
      </PageHeader>
      <Grid container spacing={3}>
        {filteredUsers.length === 0 ? (
          <Box sx={{ width: '100%', textAlign: 'center', py: 8 }}>
            <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }} />
            <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
              No users found matching your search
            </Typography>
          </Box>
        ) : (
          filteredUsers.map((user) => (
            <Grid item xs={12} sm={6} md={4} key={user._id}>
              <UserCard>
                <UserCardContent>
                  <UserAvatar
                    src={user.avatar}
                    alt={`${user.firstName} ${user.lastName}`}
                  >
                    {!user.avatar && (user.firstName?.charAt(0) || '?')}
                  </UserAvatar>
                  <Typography 
                    variant="h6" 
                    component="div" 
                    sx={{ 
                      mt: 2, 
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {user.firstName} {user.lastName}
                  </Typography>
                  <UserMetaInfo>
                    {calculateAge(user.birthday) && (
                      <Chip 
                        size="small" 
                        label={`${calculateAge(user.birthday)} years`} 
                        sx={{
                          background: 'linear-gradient(45deg, #3498db, #2c3e50)',
                          color: 'white'
                        }}
                      />
                    )}
                  </UserMetaInfo>
                  {renderUserLocation(user)}
                  {user.email && (
                    <UserInfo>
                      <EmailIcon fontSize="small" />
                      <Typography variant="body2" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '180px'
                      }}>
                        {user.email}
                      </Typography>
                    </UserInfo>
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  <Divider sx={{ width: '100%', my: 2 }} />
                  {user.partner ? (
                    <Chip label="In a Couple" color="success" sx={{ width: '100%' }} />
                  ) : (
                    <PartnerRequestButton userId={user._id} />
                  )}
                </UserCardContent>
              </UserCard>
            </Grid>
          ))
        )}
      </Grid>
    </PageContainer>
  );
};

export default Users; 